import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_LANG, LANGUAGES, STORAGE_KEY } from "./config";
import { langFromPath, stripLang, withLang, isUnlocalized } from "./paths";
import { en } from "./en";
import { tr } from "./tr";

const DICTIONARIES = { en, tr };

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key, fallback) => fallback ?? key,
  pick: (obj, field) => obj?.[field],
  formatDate: () => "",
  locale: LANGUAGES[DEFAULT_LANG].locale,
});

const readStoredLang = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && DICTIONARIES[stored] ? stored : null;
  } catch {
    return null;
  }
};

const storeLang = (lang) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private mode, blocked storage: the URL still carries the language */
  }
};

/** Dot-path lookup with a fall back to English, then to the supplied default. */
const lookup = (dict, key) =>
  key.split(".").reduce((node, part) => (node == null ? undefined : node[part]), dict);

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // The URL is the single source of truth, so the address bar and the rendered
  // language can never disagree.
  const lang = langFromPath(location.pathname);

  useEffect(() => {
    document.documentElement.lang = LANGUAGES[lang].htmlLang;
  }, [lang]);

  // A remembered explicit choice is honoured once, on an unprefixed path.
  // Browser language is never consulted.
  useEffect(() => {
    if (isUnlocalized(location.pathname)) return;
    if (lang !== DEFAULT_LANG) return;

    const stored = readStoredLang();
    if (!stored || stored === DEFAULT_LANG) return;

    navigate(withLang(location.pathname, stored) + location.search + location.hash, {
      replace: true,
    });
    // Runs on first render for a given path only; the guard above stops a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback(
    (next) => {
      if (!DICTIONARIES[next] || next === lang) return;
      storeLang(next);
      navigate(withLang(location.pathname, next) + location.search + location.hash);
    },
    [lang, location.pathname, location.search, location.hash, navigate]
  );

  const value = useMemo(() => {
    const dict = DICTIONARIES[lang] || en;

    const t = (key, fallback) => {
      const hit = lookup(dict, key);
      if (typeof hit === "string") return hit;
      const english = lookup(en, key);
      if (typeof english === "string") return english;
      return fallback ?? key;
    };

    /**
     * Reads a translated field from a data object. Turkish values live next to
     * the English ones as `<field>Tr`, so a single record holds both.
     */
    const pick = (obj, field) => {
      if (!obj) return undefined;
      if (lang !== DEFAULT_LANG) {
        const localized = obj[`${field}${lang.charAt(0).toUpperCase()}${lang.slice(1)}`];
        if (localized != null) return localized;
      }
      return obj[field];
    };

    const locale = LANGUAGES[lang].locale;

    const formatDate = (value, options = { year: "numeric", month: "long", day: "numeric" }) => {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat(locale, options).format(date);
    };

    return { lang, setLang, t, pick, formatDate, locale, path: stripLang(location.pathname) };
  }, [lang, setLang, location.pathname]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

/** Convenience hook for the common case of only needing the lookup function. */
export const useT = () => useContext(LanguageContext).t;
